import { useState } from "react";

import clsx from "clsx";

import { GenericExternalLink } from "@components/generic-external-link";
import { useUser } from "@components/user-context";
import {
  Checkbox,
  PrimaryButton,
  SecondaryButton,
  Select,
  Spinner,
} from "@components/v2";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { useForceOnboarding, useInOnboarding } from "@hooks/use-in-onboarding";
import { usePrefs } from "@utils/client-side-preferences";

import { NavButton } from "./nav-button";

function optionItemClassName(disabled: boolean) {
  return clsx(
    "block w-full h-9 px-4 flex items-center gap-2 text-sm cursor-pointer rounded",
    "hover:bg-v2-action-tertiary no-underline text-v2-primary",
    {
      "text-v2-gray/200 bg-white cursor-auto": disabled,
    },
  );
}

interface Props {
  isExpanded: boolean;
  onShowAdminModal: () => void;
}

export const AdminMenu = ({ isExpanded, onShowAdminModal }: Props) => {
  const { user, actingUser } = useUser();

  const [isProcessing, setIsProcessing] = useState(false);

  const isActing = Boolean(actingUser.actingUserId);
  const shouldShowDevelopmentAnalyticsTableSwitch =
    user.isAdmin || user.featureFlags.showDevelopmentAnalyticsTableSwitch;
  const shouldShowRetoolLink = user.isAdmin;
  const shouldShowForceOnboarding = user.isAdmin;

  const shouldShowAnonymizationSwitch =
    user.isAdmin || user.featureFlags.showAnonymizationSwitch;

  const shouldShowAdminModal = user.isAdmin;

  const shouldShowAdminMenu =
    shouldShowAnonymizationSwitch ||
    shouldShowDevelopmentAnalyticsTableSwitch ||
    shouldShowRetoolLink ||
    shouldShowForceOnboarding ||
    shouldShowAdminModal;

  if (!shouldShowAdminMenu && !isActing) {
    return null;
  }

  return (
    <Popover>
      {({ open, close }) => (
        <>
          <PopoverButton
            as={NavButton}
            showTooltip={!open}
            isExpanded={isExpanded}
            disabled={isProcessing}
            icon={
              isProcessing ? (
                <Spinner className="text-white" />
              ) : (
                <i className="fa-regular fa-screwdriver-wrench"></i>
              )
            }
          >
            Admin Tools
          </PopoverButton>
          <PopoverPanel
            as="div"
            anchor={{ to: "top start", gap: 4 }}
            transition
            className={clsx(
              "z-[9999] border border-v2-secondary rounded-md elevation-2 bg-v2-primary",
              "origin-top transition duration-200 ease-out",
              "data-[closed]:translate-y-1 data-[closed]:opacity-0",
            )}
          >
            <Content
              isProcessing={isProcessing}
              onProcessingChange={setIsProcessing}
              onShowAdminModal={onShowAdminModal}
              close={close}
            />
          </PopoverPanel>
        </>
      )}
    </Popover>
  );
};

const Content = ({
  isProcessing,
  onProcessingChange,
  onShowAdminModal,
  close,
}: {
  isProcessing: boolean;
  onProcessingChange: (val: boolean) => void;
  onShowAdminModal: () => void;
  close: () => void;
}) => {
  const { user, actingUser } = useUser();
  const prefs = usePrefs();
  const { inOnboarding } = useInOnboarding();
  const { setIsForceOnboarding } = useForceOnboarding();

  const currentDataset = prefs.useDevelopmentAnalyticsData
    ? prefs.developmentTable
    : "production";

  const [dataset, setDataset] = useState(currentDataset);

  const isActing = Boolean(actingUser.actingUserId);
  const shouldShowDevelopmentAnalyticsTableSwitch =
    user.isAdmin || user.featureFlags.showDevelopmentAnalyticsTableSwitch;
  const shouldShowRetoolLink = user.isAdmin;
  const shouldShowForceOnboarding = user.isAdmin;

  const shouldShowAnonymizationSwitch =
    user.isAdmin || user.featureFlags.showAnonymizationSwitch;

  const shouldShowAdminModal = user.isAdmin;

  return (
    <div className="flex">
      <div className="w-52 p-[1px]">
        {isActing && (
          <button
            className={optionItemClassName(isProcessing)}
            onClick={() => {
              close();
              onProcessingChange(true);
              actingUser.setActingUserId(null);
              window.location.reload();
            }}
          >
            <Checkbox size="sm" checked={isActing} />
            Impersonating
          </button>
        )}
        {shouldShowAnonymizationSwitch && (
          <button
            className={optionItemClassName(isProcessing)}
            onClick={async () => {
              close();
              onProcessingChange(true);
              await prefs.setDemoMode(!prefs.isDemoMode);
              window.location.reload();
            }}
          >
            <Checkbox size="sm" defaultChecked={prefs.isDemoMode} />
            Anonymize data
          </button>
        )}
        {inOnboarding && shouldShowForceOnboarding && (
          <button
            className={optionItemClassName(isProcessing)}
            onClick={async () => {
              close();
              onProcessingChange(true);
              await setIsForceOnboarding(false);
              window.location.reload();
            }}
          >
            Exit User Onboarding
          </button>
        )}
        {!inOnboarding && shouldShowForceOnboarding && (
          <button
            className={optionItemClassName(isProcessing)}
            onClick={async () => {
              close();
              onProcessingChange(true);
              await setIsForceOnboarding(true);
              window.location.reload();
            }}
          >
            Go to User Onboarding
          </button>
        )}
        {shouldShowRetoolLink && (
          <GenericExternalLink
            className={optionItemClassName(isProcessing)}
            href={`https://northbeam.retool.com/apps/fbf147e0-c253-11eb-8912-ef20e112236b/Northbeam#userId=${user.displayUserId}`}
            onClick={() => {
              close();
            }}
          >
            Open Retool
          </GenericExternalLink>
        )}
        {shouldShowAdminModal && (
          <button
            className={optionItemClassName(isProcessing)}
            onClick={() => {
              close();
              onShowAdminModal();
            }}
          >
            BQ SQL Logs
          </button>
        )}
      </div>
      {shouldShowDevelopmentAnalyticsTableSwitch && (
        <div
          className={clsx(
            "w-[380px] bg-v2-highlight px-6 py-4 flex flex-col justify-between",
            "border-l border-v2-tertiary",
          )}
        >
          <div className="mb-6">
            <div className="mb-1 text-xs font-semibold">
              Select the dataset to use
            </div>
            <Select
              className="w-full"
              buttonClassName="w-full"
              value={dataset}
              options={[
                { label: "production", value: "production" },
                ...prefs.availableDevelopmentTables.map((table) => {
                  return {
                    label: table,
                    value: table,
                  };
                }),
              ]}
              onChange={(value) => {
                if (value) {
                  setDataset(value);
                }
              }}
            />
          </div>
          <div className="flex items-center justify-end gap-2">
            <SecondaryButton size="sm" onClick={() => close()}>
              CANCEL
            </SecondaryButton>
            <PrimaryButton
              size="sm"
              disabled={dataset === currentDataset}
              onClick={async () => {
                close();
                onProcessingChange(true);

                if (dataset === "production") {
                  await prefs.setUseDevelopmentAnalyticsData(false);
                } else {
                  prefs.setDevelopmentTable(dataset);
                  await prefs.setUseDevelopmentAnalyticsData(true);
                }

                window.location.reload();
              }}
            >
              SWITCH DATASETS
            </PrimaryButton>
          </div>
        </div>
      )}
    </div>
  );
};
